import {
  Tooltip,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { memo } from 'react';
import { Close, HelpOutline, PhoneInTalkOutlined } from '@mui/icons-material';
import { getColor } from '../../../../../assets/theme';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import AutoCompleteController from '../../../../../components/form/controller/autocomplete.controller';
import { profileAddContactNumberSchema } from '../../../interface/fo-profile-schema';
import { useSelector } from 'react-redux';
import useToast from '../../../../../hooks/useToast';
import { RootState } from '../../../../../store';
import { useUpdateContactInformationsMutation } from '../../../../../api/front-office/profile.api.rtk';
import { SHA512 } from 'crypto-js';
import { ModalWrapper } from '../../../../../hooks/useModal';

export interface AddContact {
  contactNumber: string;
  type: string;
  primary: string;
}

const AddContactNumberModal = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const selectedPropertyID = useSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );
  const { toast } = useToast();
  const { primary, textGray } = getColor();
  const optionsType = ['Mobile', 'Phone', 'Fax'];
  const optionsPrimary = ['Yes', 'No'];

  const [updateContactInformations] = useUpdateContactInformationsMutation();

  const handleDrawerClose = () => {
    onClose();
  };

  const handleAddContact: SubmitHandler<AddContact> = async (data) => {
    const { contactNumber, type, primary } = data;

    const contactNo = contactNumber;
    const CIType = 'contact';
    const contactType = type;
    const isPrimary = primary === 'Yes';
    const action = 'update';
    const contactInformationsLength = 1;

    const signature = SHA512(
      selectedPropertyID +
        profileID +
        CIType +
        contactNo +
        +!!isPrimary +
        contactType +
        action,
    ).toString();

    const profileSignature = SHA512(
      selectedPropertyID + profileID + contactInformationsLength,
    ).toString();

    const contact = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            profiles: [
              {
                profile: {
                  profileID: profileID,
                  contactInformations: [
                    {
                      contactInformation: {
                        CIType: CIType,
                        value: contactNo,
                        type: contactType,
                        isPrimary: isPrimary,
                        action: 'update',
                        signature: signature,
                      },
                    },
                  ],
                  signature: profileSignature,
                },
              },
            ],
          },
        },
      ],
    };

    await updateContactInformations(contact)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Contact added successfully!',
        });
        reset();
        handleDrawerClose();
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      });
  };

  const methods = useForm<AddContact>({
    defaultValues: {
      contactNumber: '',
      type: '',
      primary: '',
    },
    resolver: yupResolver<AddContact>(profileAddContactNumberSchema),
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  return (
    <ModalWrapper
      hideBackdrop
      disableOnClose
      onClose={onClose}
      modalDisclosure={{
        isOpen: isOpen,
        onClose: onClose,
        onToggle: () => {},
        onOpen: () => {},
      }}
      boxStyle={{ width: '398px', height: '376px', maxHeight: '90vh' }}
    >
      <Box p={3} sx={{ position: 'relative' }}>
        <form id='add-contact' onSubmit={handleSubmit(handleAddContact)}>
          <Stack
            direction='row'
            justifyContent='space-between'
            borderBottom='1px solid #EEE'
            p='10px 0'
          >
            <Typography variant='h5' component='div' sx={{ color: '#000000' }}>
              Add Contact
              <Tooltip title='Learn More'>
                <IconButton
                  sx={{
                    border: 0,
                    color: textGray,
                    bgcolor: 'transparent',
                    ':hover': { color: primary, bgcolor: 'transparent' },
                  }}
                  disableRipple
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>

          <Box mb={2} />

          <InputFieldsController
            label='Contact Number'
            name='contactNumber'
            control={control}
            errors={errors}
            size='medium'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <PhoneInTalkOutlined />
                </InputAdornment>
              ),
            }}
            borderErrorOnly
          />
          <AutoCompleteController
            control={control}
            errors={errors}
            label='Type'
            name='type'
            options={optionsType || []}
            size='medium'
            borderErrorOnly
          />
          <AutoCompleteController
            control={control}
            errors={errors}
            label='Is Primary'
            name='primary'
            options={optionsPrimary || []}
            size='medium'
            borderErrorOnly
          />
        </form>

        <Stack direction='row' gap={1} justifyContent='flex-end' mt={2}>
          <Button
            variant='outlined'
            onClick={onClose}
            sx={{ width: '74px', height: '36px' }}
          >
            Close
          </Button>
          <Button
            form='add-contact'
            type='submit'
            variant='contained'
            sx={{ width: '74px', height: '36px' }}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </ModalWrapper>
  );
};

export default memo(AddContactNumberModal);
